import React from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <main className="max-w-3xl mx-auto px-5 py-16" data-testid="privacy-page">
      <Link to="/" className="inline-flex items-center gap-2 fl-mono text-[11px] tracking-widest uppercase text-slate-400 hover:text-emerald-500 mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Fliboe
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <Shield className="w-6 h-6 text-emerald-500" />
        <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-emerald-500">// Legal</div>
      </div>
      <h1 className="fl-display text-4xl sm:text-5xl font-extrabold tracking-tighter">Privacy Policy</h1>
      <p className="text-slate-400 mt-3 text-sm">Last updated: February 2026</p>

      <div className="mt-10 space-y-8 text-slate-300 text-sm leading-relaxed">
        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">1. The short version</h2>
          <p>
            We collect the absolute minimum needed to run an internet radio platform.
            We don't sell your data. We don't track you across the web. We use HTTPS everywhere.
            You can export your data or delete your account anytime via your Studio settings.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">2. What we collect</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Account:</strong> email, name, optional profile picture, password (hashed with bcrypt, never stored in plaintext).</li>
            <li><strong>Station data:</strong> station name, tagline, cover art, MP3 files you upload, schedule, Tip Jar URL.</li>
            <li><strong>Listener analytics:</strong> anonymous listener counts, country code (from IP — not the IP itself), play timestamps. We do not log individual listening history.</li>
            <li><strong>Referrals:</strong> a 6-char code per user + which user invited whom (to honor reward credits).</li>
            <li><strong>Push notifications:</strong> the browser-issued subscription endpoint for stations you opted into.</li>
            <li><strong>Support chat:</strong> the messages you send to the AI Support widget (used to give better answers in the same session).</li>
          </ul>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">3. What we DO NOT collect</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Your IP address (we keep only the 2-letter country code derived from it).</li>
            <li>Browsing history outside Fliboe.</li>
            <li>Phone number, address, or government ID.</li>
            <li>Tip Jar transaction details — those happen on PayPal / Cash App / Venmo, not on our servers.</li>
          </ul>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">4. Third parties</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Google OAuth</strong> (if you sign in with Google): your name, email, and avatar URL.</li>
            <li><strong>OpenAI</strong> (for AI DJ + AI Support): short station/track snippets and your chat messages are sent to OpenAI to generate audio / replies. OpenAI states they don't train on API data.</li>
            <li><strong>Cloudflare</strong> (CDN / edge): standard request logs at the edge.</li>
            <li><strong>PostHog</strong> (product analytics): anonymous page-view and click events with no personal identifiers tied.</li>
          </ul>
          <p className="mt-2">We never sell, rent, or share your personal data with advertisers or data brokers.</p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">5. Cookies</h2>
          <p>
            One session cookie (HTTP-only, Secure, SameSite=None) to keep you signed in. Optional
            localStorage entries to remember your install-prompt dismissal, push subscription, and
            referral code. No third-party advertising cookies.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">6. Your rights (GDPR / CCPA)</h2>
          <p>Wherever you live, you can:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1.5">
            <li><strong>Export</strong> everything we have on you — Studio → Account → Export my data (JSON download).</li>
            <li><strong>Delete</strong> your account and all associated stations / tracks / files — Studio → Account → Delete my account. Irreversible.</li>
            <li><strong>Correct</strong> your name, email, password — Studio settings.</li>
            <li>Ask questions about your data via the AI Support chat at the bottom-right.</li>
          </ul>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">7. Data retention</h2>
          <p>
            We keep your data for as long as your account is active. When you delete your account,
            we purge user record, stations, tracks, MP3 files, and DJ voice drops within 7 days.
            Anonymous aggregate analytics may persist (e.g., total listener-minutes for that month).
            Daily admin backups are retained for up to 14 days.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">8. Security</h2>
          <p>
            Passwords are hashed with bcrypt. JWTs are signed with HS256. All traffic is HTTPS.
            File uploads are stored in MongoDB GridFS on a managed Atlas cluster with encryption at rest.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">9. Children</h2>
          <p>
            Fliboe is not directed at children under 13. We don't knowingly collect data from anyone
            under 13. If you believe a child has signed up, contact us via support and we'll delete
            the account.
          </p>
        </section>

        <section>
          <h2 className="fl-display text-xl font-bold text-white mb-2">10. Contact</h2>
          <p>
            For data requests, privacy questions, or to report a concern, use the AI Support chat at
            the bottom-right of every page. A human team member reviews privacy escalations.
          </p>
        </section>
      </div>
    </main>
  );
}
